import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom';
import type { Me } from '@unitly/contracts';
import { OrgContext, type OrgCtx, type QuickAddDefaults } from './org-context.tsx';
import { useOrg } from '../api/queries.ts';
import { todayIn } from '../lib/dates.ts';
import { rememberOrg } from '../lib/theme.ts';
import { Sidebar, TabBar } from '../features/shell/Chrome.tsx';
import { TaskDetailSheet } from '../features/tasks/TaskDetail.tsx';
import { QuickAddSheet } from '../features/tasks/QuickAdd.tsx';
import { CommandPalette } from '../features/shell/CommandPalette.tsx';
import { CreateWorkspaceSheet } from '../features/shell/CreateWorkspace.tsx';
import { ContentSheet } from '../features/editorial/ContentSheet.tsx';
import { HomeCustomizeSheet } from '../features/home/HomeCustomizeSheet.tsx';

export function OrgLayout({ me }: { me: Me }) {
  const { orgSlug } = useParams();
  const org = me.organizations.find((o) => o.slug === orgSlug);
  const detail = useOrg(org?.id ?? '');
  const [params, setParams] = useSearchParams();
  const [quickAdd, setQuickAdd] = useState<QuickAddDefaults | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [createWs, setCreateWs] = useState(false);
  const [homeCustomizeOpen, setHomeCustomizeOpen] = useState(false);
  const [today, setToday] = useState(() => todayIn(me.user.timeZone));

  useEffect(() => {
    if (org) rememberOrg(org.slug);
  }, [org]);

  // Keep "today" right across midnight and when the app comes back to the foreground.
  useEffect(() => {
    const refresh = () => setToday(todayIn(me.user.timeZone));
    const id = setInterval(refresh, 60_000);
    document.addEventListener('visibilitychange', refresh);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', refresh); };
  }, [me.user.timeZone]);

  // ⌘K / Ctrl+K opens search, "c" creates a task (when not typing)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = /INPUT|TEXTAREA|SELECT/.test((e.target as HTMLElement).tagName) || (e.target as HTMLElement).isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSearchOpen(true); }
      else if (!typing && !e.metaKey && !e.ctrlKey && !e.altKey && e.key === 'c') { e.preventDefault(); setQuickAdd({}); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const taskId = params.get('task');
  const openTask = useCallback((id: string) => {
    setParams((p) => { const n = new URLSearchParams(p); n.set('task', id); return n; });
  }, [setParams]);
  const closeTask = useCallback(() => {
    setParams((p) => { const n = new URLSearchParams(p); n.delete('task'); return n; }, { replace: true });
  }, [setParams]);
  const contentId = params.get('content');
  const openContent = useCallback((id: string) => {
    setParams((p) => { const n = new URLSearchParams(p); n.set('content', id); n.delete('task'); return n; });
  }, [setParams]);
  const closeContent = useCallback(() => {
    setParams((p) => { const n = new URLSearchParams(p); n.delete('content'); return n; }, { replace: true });
  }, [setParams]);

  const ctx = useMemo<OrgCtx | null>(() => org ? ({
    orgId: org.id,
    slug: org.slug,
    org,
    detail: detail.data,
    me,
    timeZone: me.user.timeZone,
    today,
    base: `/o/${org.slug}`,
    openTask,
    openContent,
    openQuickAdd: (d) => setQuickAdd(d ?? {}),
    openSearch: () => setSearchOpen(true),
    openCreateWorkspace: () => setCreateWs(true),
    openHomeCustomize: () => setHomeCustomizeOpen(true),
  }) : null, [org, detail.data, me, today, openTask, openContent]);

  if (!org || !ctx) return <Navigate to="/" replace />;

  return (
    <OrgContext.Provider value={ctx}>
      <div className="shell">
        <Sidebar />
        <main className="main" id="main">
          <Outlet />
        </main>
        <TabBar />
      </div>
      <TaskDetailSheet taskId={taskId} onClose={closeTask} />
      <ContentSheet contentId={contentId} onClose={closeContent} />
      <QuickAddSheet open={quickAdd !== null} defaults={quickAdd ?? {}} onClose={() => setQuickAdd(null)} />
      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <CreateWorkspaceSheet open={createWs} onClose={() => setCreateWs(false)} />
      <HomeCustomizeSheet open={homeCustomizeOpen} onClose={() => setHomeCustomizeOpen(false)} />
    </OrgContext.Provider>
  );
}
